/*
 * Act on many rows at once.
 *
 * The API has no bulk endpoints, so this runs the existing single-item request
 * once per selected row, a few at a time, behind one progress bar. Rows that
 * cannot be processed are reported back with the reason instead of failing the
 * whole run, so one blocked row never undoes the rest.
 *
 * Usage:
 *
 *   import bulkActions from "@/mixins/bulkActions";
 *   export default { mixins: [bulkActions], ... }
 *
 *   this.runBulk({
 *     items: this.selected,
 *     title: this.$t("delete_selected"),
 *     confirmText: this.$t("confirm_bulk_delete", { count: this.selected.length }),
 *     label: (bus) => bus.license,
 *     skip: (bus) => (bus.driver ? this.$t("bus_has_driver") : null),
 *     request: (bus) => axios.delete(`/buses/${bus.id}`),
 *     onSuccess: (bus) => this.removeFromList(this.buses, bus),
 *   });
 */

//a handful at a time: fast enough to feel instant on a long selection, gentle
//enough not to flood a server that is doing real work per request
const CONCURRENCY = 4;

export default {
  data() {
    return {
      selected: [],
      bulkRunning: false,
      bulkDone: 0,
      bulkTotal: 0,
      bulkFailures: [],
      bulkResultDialog: false,
      bulkResultTitle: "",
    };
  },

  computed: {
    hasSelection() {
      return this.selected && this.selected.length > 0;
    },
    bulkProgress() {
      if (this.bulkTotal === 0) return 0;
      return Math.round((this.bulkDone / this.bulkTotal) * 100);
    },
  },

  methods: {
    clearSelection() {
      this.selected = [];
    },

    /** Remove an item from a list by identity or id. */
    removeFromList(list, item) {
      let index = list.indexOf(item);
      if (index === -1 && item && item.id !== undefined) {
        index = list.findIndex((row) => row.id === item.id);
      }
      if (index !== -1) list.splice(index, 1);
    },

    /**
     * Confirm, then run `request` over every item.
     *
     * @param {Array}    options.items
     * @param {string}   options.title        heading for the confirm and summary
     * @param {string}   options.confirmText  body of the confirm dialog
     * @param {Function} options.label        item => name shown in the report
     * @param {Function} [options.skip]       item => reason to not even try, or null
     * @param {Function} options.request      item => Promise
     * @param {Function} [options.onSuccess]  item => void, per successful row
     * @param {Function} [options.onFinish]   ({succeeded, failed}) => void
     */
    runBulk(options) {
      const items = (options.items || []).slice();
      if (items.length === 0 || this.bulkRunning) {
        return Promise.resolve();
      }

      return this.$confirmAction({
        title: options.title,
        text: options.confirmText,
        icon: "warning",
        confirmText: options.confirmText ? this.$t("yes") : undefined,
      }).then((confirmed) => {
        if (!confirmed) return;
        return this.executeBulk(items, options);
      });
    },

    executeBulk(items, options) {
      const label = options.label || ((item) => item.id);
      const skip = options.skip || (() => null);

      this.bulkRunning = true;
      this.bulkDone = 0;
      this.bulkTotal = items.length;
      this.bulkFailures = [];
      this.bulkResultTitle = options.title || "";

      const queue = items.slice();
      const succeeded = [];

      const processNext = () => {
        const item = queue.shift();
        if (item === undefined) return Promise.resolve();

        const blocked = skip(item);
        if (blocked) {
          this.bulkFailures.push({ name: label(item), reason: blocked });
          this.bulkDone += 1;
          return processNext();
        }

        return Promise.resolve(options.request(item))
          .then(() => {
            succeeded.push(item);
            if (options.onSuccess) options.onSuccess(item);
          })
          .catch((error) => {
            this.bulkFailures.push({
              name: label(item),
              reason: this.$apiError(error),
            });
          })
          .then(() => {
            this.bulkDone += 1;
            return processNext();
          });
      };

      const workers = [];
      for (let i = 0; i < Math.min(CONCURRENCY, items.length); i++) {
        workers.push(processNext());
      }

      return Promise.all(workers).then(() => {
        this.bulkRunning = false;
        this.clearSelection();
        this.reportBulk(succeeded.length);
        if (options.onFinish) {
          options.onFinish({ succeeded: succeeded, failed: this.bulkFailures });
        }
      });
    },

    reportBulk(successCount) {
      const failureCount = this.bulkFailures.length;

      if (successCount > 0) {
        this.$notifySuccess(
          this.$t("bulk_done", { count: successCount })
        );
      }

      //only interrupt with a dialog when something actually needs reading
      if (failureCount > 0) {
        this.bulkResultDialog = true;
      }
    },
  },
};
